import { AttachFile, Close, CloudDone, Warning } from '@mui/icons-material';
import {
  Box,
  Button,
  Typography,
  Grid,
  IconButton,
  CircularProgress,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { resizeJPEGFiles } from '../helpers/image';

const PREFIX = 'GenFileUpload';

const classes = {
  fileChooser: `${PREFIX}-fileChooser`,
  dragEnter: `${PREFIX}-dragEnter`,

  fileType: `${PREFIX}-fileType`,
  fileDone: `${PREFIX}-fileDone`,
  fileError: `${PREFIX}-fileError`,
  invalidDragging: `${PREFIX}-invalidDragging`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  display: 'block',
  background: theme.palette.grey[100],
  borderRadius: '4px',
  border: `1px solid ${theme.palette.divider}`,

  [`& .${classes.fileChooser}`]: {
    display: 'flex',
    alignItems: 'center',
    background: theme.palette.grey[200],
    border: `1px dashed ${theme.palette.divider}`,
    mixBlendMode: 'normal',
    boxSizing: 'border-box',
    borderRadius: '4px',
  },

  [`& .${classes.dragEnter}`]: {
    background: theme.palette.grey[300],
    border: `1px solid ${theme.palette.divider}`,
  },

  [`& .${classes.invalidDragging}`]: {
    cursor: 'not-allowed',
    background: theme.palette.grey[200],
  },
}));

const File = styled(Grid)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  overflow: 'hidden',

  background: theme.palette.common.white,
  border: `1px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 0, 1, 2),
  mixBlendMode: 'normal',
  boxSizing: 'border-box',
  borderRadius: '4px',

  [`& .${classes.fileType}`]: {
    color: theme.palette.primary.dark,
    fontWeight: 900,
  },

  [`& .${classes.fileDone}`]: {
    color: theme.palette.success.main,
  },

  [`& .${classes.fileError}`]: {
    color: theme.palette.error.main,
  },
}));

const FileStatusIcons = {
  success: <CloudDone fontSize="small" className={classes.fileDone} />,
  error: <Warning fontSize="small" className={classes.fileError} />,
  uploading: <CircularProgress size={'1rem'} />,
};

const FileItem = ({
  variant,
  fileName,
  status,
  details,
  type,
  onDeleteFile = () => {},
}) => {
  const fileNameTypographyVariantByVariant = {
    normal: 'h6',
    dense: 'subtitle2',
  };

  const detailsTypographyVariantByVariant = {
    normal: 'body1',
    dense: 'caption',
  };

  return (
    <File item>
      <Box>
        <Typography variant="h5" className={classes.fileType}>
          {`${type}`.toUpperCase()}
        </Typography>
      </Box>
      <Stack sx={{ flexGrow: 8 }} pl={3} overflow="hidden">
        <Typography
          variant={fileNameTypographyVariantByVariant[variant]}
          overflow="hidden"
          whiteSpace="nowrap"
          textOverflow="ellipsis"
        >
          {fileName}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {FileStatusIcons[status]}
          <Typography
            pl={1}
            variant={detailsTypographyVariantByVariant[variant]}
          >
            {details}
          </Typography>
        </Box>
      </Stack>
      <Box>
        <IconButton aria-label="close" size="medium" onClick={onDeleteFile}>
          <Close />
        </IconButton>
      </Box>
    </File>
  );
};

const GenFileChooser = ({
  variant = 'normal',
  accept = [],
  files = [],
  multiple = false,
  maxSize = null,
  onChange = () => {},
  onDeleteFile = file => {},
}) => {
  const { t } = useTranslation('global');
  const inputFile = useRef(null);
  const [inDropZone, setInDropZone] = useState(false);
  const [invalidDrag, setInvalidDrag] = useState(false);

  const onDragEnter = event => {
    event.preventDefault();
    setInDropZone(true);
    setInvalidDrag(areAllFilesInvalid([...event.dataTransfer.items]));
  };

  const areAllFilesInvalid = filesDropped =>
    countFilesInvalid(filesDropped.length) ||
    !filterFilesByTypeAndSize(filesDropped).length;

  const countFilesInvalid = length => !multiple && length > 1;

  const filterFilesByTypeAndSize = filesDropped =>
    filterFilesBySize(filterFilesByTypes(filesDropped));

  const filterFilesByTypes = filesDropped =>
    filesDropped.filter(file => accept.some(type => file.type.match(type)));

  const filterFilesBySize = filesDropped =>
    filesDropped.filter(file => !maxSize || (file.size || 0) <= maxSize);

  const onDragleave = event => {
    event.preventDefault();
    setInDropZone(false);
    setInvalidDrag(false);
  };

  const onDrop = async event => {
    event.preventDefault();
    setInDropZone(false);
    setInvalidDrag(false);
    await dispatchEventChange([...event.dataTransfer.files]);
  };

  const onChangeInput = async event => {
    await dispatchEventChange([...event.target.files]);
  };

  const dispatchEventChange = async filesSelected => {
    if (areAllFilesInvalid(filesSelected)) return;

    const filteredFiles = filterFilesByTypeAndSize(filesSelected);
    const resizedFiles = await resizeJPEGFiles(filteredFiles);
    onChange(resizedFiles);
  };

  const paddingBoxByVariant = {
    normal: 4,
    dense: 1,
  };

  const titleTypographyVariantByVariant = {
    normal: 'h6',
    dense: 'subtitle2',
  };

  const boxPaddingByVariant = {
    dense: theme => theme.spacing(2, 2),
    normal: theme => theme.spacing(4, 6),
  };

  const boxFilesPTByVariant = {
    dense: 1,
    normal: 3,
  };

  const gridTemplateByVariant = {
    dense: 'repeat(1, 1fr)',
    normal: 'repeat(2, 1fr)',
  };

  return (
    <StyledBox p={paddingBoxByVariant[variant]}>
      <input
        type="file"
        multiple={multiple}
        onChange={onChangeInput}
        ref={inputFile}
        accept={accept.join(',')}
        hidden
      />
      <Box
        className={`${classes.fileChooser} ${inDropZone && classes.dragEnter} ${
          invalidDrag && classes.invalidDragging
        }`}
        p={boxPaddingByVariant[variant]}
        role="fileDragZone"
        onDragEnter={onDragEnter}
        onDragOver={onDragEnter}
        onDragLeave={onDragleave}
        onDrop={onDrop}
      >
        <Box sx={{ flexGrow: 1 }}>
          <Typography>{t('fileUpload.dragZoneMessage')}</Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<AttachFile />}
          onClick={() => inputFile.current.click()}
        >
          {t('fileUpload.attachFile')}
        </Button>
      </Box>
      <Box pt={boxFilesPTByVariant[variant]}>
        <Typography variant={titleTypographyVariantByVariant[variant]}>
          {t('fileUpload.files')}
        </Typography>
        <Grid
          container
          display="grid"
          columnGap={3}
          rowGap={1}
          gridTemplateColumns={gridTemplateByVariant[variant]}
        >
          {files.map((file, index) => (
            <FileItem
              key={file.fileId || file.fileName || index}
              {...file}
              onDeleteFile={() => onDeleteFile(file)}
            />
          ))}
        </Grid>
      </Box>
    </StyledBox>
  );
};

export default GenFileChooser;
